import React, { useContext, useEffect, useRef } from "react";
import "../../../stylesheets/booking_calendar.scss";

// Shared date-and-time picker layout used by every booking flow.
// It only draws: each caller keeps its own logic (which days are enabled,
// how times load, what Apply does), so behavior stays exactly as before.
//
// days: [{ key, blank, num, disabled, selected, marked, ariaLabel, onClick }]
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const Chevron = ({ dir }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d={dir === "left" ? "M15 6l-6 6 6 6" : "M9 6l6 6-6 6"} />
  </svg>
);

// A booking modal that wants the calendar shown as one of its own steps
// (same box, its own footer buttons) wraps its content in this provider.
// value: { setActions(actions | null) } — actions: { onApply, onCancel, applyDisabled, applyLabel }
export const BookingInlineContext = React.createContext(null);

export default function BookingCalendar(props) {
  const inlineHost = useContext(BookingInlineContext);
  const {
    title, subtitle, onClose, headerExtra,
    monthLabel, onPrevMonth, onNextMonth, prevDisabled,
    days, loadingDays, showLegend,
    timesHeading, timezoneNote, times, timesLoading, emptyTimesText,
    footerLink, summary, applyLabel, cancelLabel, onApply, applyDisabled, children
  } = props;
  const hasTimes = Array.isArray(times) ? times.length > 0 : !!times;

  // Inline: hand Apply/Cancel to the host modal's footer (latest handlers via ref).
  const handlers = useRef({});
  handlers.current = { onApply, onClose };
  useEffect(() => {
    if (!inlineHost) return;
    inlineHost.setActions({
      onApply: onApply ? () => handlers.current.onApply && handlers.current.onApply() : null,
      onCancel: () => handlers.current.onClose && handlers.current.onClose(),
      applyDisabled: !!applyDisabled,
      applyLabel: applyLabel || null
    });
  }, [inlineHost, !!onApply, !!applyDisabled, applyLabel]);
  useEffect(() => () => { if (inlineHost) inlineHost.setActions(null); }, [inlineHost]);

  const header = <div className="bkCal-header">
    <div className="bkCal-headings projectLocationHeadingGrouping modalHeader">
      {/* Same heading classes as the other booking steps, so it matches them. */}
      <div className="settingsComponentHeading serviceChoiceHeading bkCal-title" role="heading" aria-level="2">{title}</div>
      {subtitle && <p className="bkCal-subtitle">{subtitle}</p>}
    </div>
    {!inlineHost && <button type="button" className="bkCal-close" aria-label="Close" onClick={onClose}>
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
    </button>}
  </div>;

  const body = <div className="bkCal-body">
    {headerExtra}
    {children}
    <div className="bkCal-columns">
      <div className="bkCal-calendar">
        <div className="bkCal-monthbar">
          <button type="button" className="bkCal-arrow" aria-label="Previous month" onClick={onPrevMonth} disabled={prevDisabled}><Chevron dir="left" /></button>
          <div className="bkCal-month">{monthLabel}</div>
          <button type="button" className="bkCal-arrow" aria-label="Next month" onClick={onNextMonth}><Chevron dir="right" /></button>
        </div>
        <div className="bkCal-weekdays" aria-hidden="true">
          {WEEKDAYS.map(w => <div key={w}>{w}</div>)}
        </div>
        <div className="bkCal-gridWrap">
          <div className={`bkCal-grid${loadingDays ? " is-loading" : ""}`}>
            {days.map(d => d.blank
              ? <div key={d.key} className="bkCal-blank" />
              : <button type="button" key={d.key}
                  className={`bkCal-day${d.selected ? " is-selected" : ""}${d.disabled ? " is-disabled" : ""}${d.marked ? " is-marked" : ""}`}
                  disabled={d.disabled} aria-pressed={d.selected ? "true" : "false"} aria-label={d.ariaLabel}
                  onClick={d.onClick}>
                  <span>{d.num}</span>
                  <span className="bkCal-dot" aria-hidden="true" />
                </button>)}
          </div>
          {loadingDays && <div className="bkCal-loading">Checking availability…</div>}
        </div>
        {showLegend && <div className="bkCal-legend">
          <span><span className="bkCal-legendDot" />Times available</span>
          <span><span className="bkCal-legendLine" />Fully booked</span>
        </div>}
      </div>

      <div className="bkCal-times">
        {timesHeading && <div className="bkCal-timesHeading">{timesHeading}</div>}
        {timezoneNote && <div className="bkCal-tz">{timezoneNote}</div>}
        {timesLoading
          ? <div className="bkCal-empty">Loading times…</div>
          : hasTimes ? <div className="bkCal-timeGrid">{times}</div>
          : <div className="bkCal-empty">{emptyTimesText || "No times for this date"}</div>}
        {footerLink}
      </div>
    </div>
  </div>;

  // Inline: drawn as a step inside the booking modal; the modal's own footer
  // (Prev Step / Next Step) drives Cancel / Apply.
  if (inlineHost) {
    return <div className="bkCal bkCal--inline" aria-label={title}>
      {header}
      {body}
      {summary && <div className="bkCal-inlineSummary">Your shoot: <strong>{summary}</strong></div>}
    </div>;
  }

  return (
    <div className="modal-background bkCal-overlay" onClick={onClose}>
      <div className="bkCal" role="dialog" aria-modal="true" aria-label={title} onClick={e => e.stopPropagation()}>
        {header}
        {body}
        <div className="bkCal-footer">
          {summary && <div className="bkCal-summary">
            <span>Your shoot</span>
            <strong>{summary}</strong>
          </div>}
          <div className={`bkCal-actions${onApply ? "" : " is-viewOnly"}`}>
            <button type="button" className="bkCal-cancel" onClick={onClose}>{cancelLabel || "Cancel"}</button>
            {onApply && <button type="button" className="bkCal-apply" onClick={onApply} disabled={applyDisabled}>{applyLabel || "Apply"}</button>}
          </div>
        </div>
      </div>
    </div>
  );
}

// Builds the grid cells (leading blanks + days) for a month of Date objects.
export const monthCells = (dates, cellFor) => {
  if (!dates || dates.length === 0) return [];
  const first = dates[0];
  const blanks = Array.from({ length: first.getDay() }, (_, i) => ({ key: `blank-${i}`, blank: true }));
  return blanks.concat(dates.map((date, index) => ({ key: `d-${index}`, num: date.getDate(), ...cellFor(date, index) })));
};
